import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { removeEmpty } from '../../../service/table.service';

const { logError } = RaygunErrorHandlerService();

export class AdminJournal {
  id: string | null = null;
  organization_ids: string[] | null = null;
  organizations: string[] | null = null;
  name: string | null = null;
  client_facing_name_alias: string[] | null = null;
  short_name: string | null = null;
  internal_name: string | null = null;
  status_medium_risk: number | null = null;
  status_high_risk: number | null = null;
  jump_high_risk: number | null = null;
  jump_medium_risk: number | null = null;
  total_count: number = 0;
}

type AdminJournalSliceType = {
  adminJournalData: AdminJournal[];
  requestAdminJournalParam: RequestParams<AdminJournal>;
  loadingAdminJournals: boolean;
  adminAPIErrorText: string;
  adminJournal: AdminJournal;
};

const initialState: AdminJournalSliceType = {
  adminJournalData: [],
  requestAdminJournalParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminJournal>('name', 'ascend'),
    filteredInfo: {},
  },
  loadingAdminJournals: false,
  adminAPIErrorText: '',
  adminJournal: new AdminJournal(),
};

export const fetchAdminJournals = createAsyncThunk<AdminJournal[], undefined, AsyncThunkConfig>(
  'adminJournal/fetchAdminJournals',
  async (_, thunkAPI) => {
    try {
      const { adminJournalSlice } = thunkAPI.getState();
      const response = (await axios.post('v3_get_all_product_admin', adminJournalSlice.requestAdminJournalParam, {
        signal: thunkAPI.signal,
      })) as AdminJournal[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminJournalSlice', 'fetchAdminJournals']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminJournalSlice = createSlice({
  name: 'adminJournal',
  initialState,
  reducers: {
    handleJournalTableChange: (state, action: PayloadAction<TableChange<AdminJournal>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminJournal>;
      if (!sorter.order) {
        sorter.order = null;
      }
      state.requestAdminJournalParam.pagination = action.payload.pagination;
      state.requestAdminJournalParam.sortedInfo = sorter;
      state.requestAdminJournalParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminJournalParam = initialState.requestAdminJournalParam;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminJournals.pending, (state, _action) => {
        state.loadingAdminJournals = true;
      })
      .addCase(fetchAdminJournals.fulfilled, (state, action) => {
        state.adminJournalData = action.payload;
        state.loadingAdminJournals = false;
      })
      .addCase(fetchAdminJournals.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingAdminJournals = false;
          state.adminAPIErrorText = (action.payload as ErrorMessage)?.displayText;
        }
      });
  },
});

export const { handleJournalTableChange, clearFilter } = adminJournalSlice.actions;
